import React, { useContext, useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";
import searchIcon from "../../../assets/search_icon.png";
import previousIcon from "../../../assets/previous_icon.png";
import nextIcon from "../../../assets/next_icon.png";
import axios from "axios";
import saveIcon from "../../../assets/save_icon.png";
import PaperOrderListWithInput from "../PaperOrderListWithInput";
import AreYouSureModal from "../../../components/AreYouSureModal";
import useLanguage from "../../../utils/useLanguage";
import PaperRulers from "../PaperRulers";
import BackToDashboard from "../../../components/BackToDashboard";
import { Loader } from "lucide-react";
import PrintableOrderView from "../../fabric-store/PrintableOrderView";
import PageHeadline from "../../../components/PageHeadline";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import PageWrapper from "../../../components/PageWrapper";
import { UserAuth } from "../../../utils/AuthContext";
import useRequest from "../../../utils/useRequest";
import SectionDivider from "../../../components/SectionDivider";
import { formatDate } from "../../../utils/toDate";
import InternalPaperRulers from "./InternalPaperRulers";
import InternalPaperOrderListWithInput from "./InternalPaperOrderListWithInput";
import InternalPrintableOrderView from "./InternalPrintableOrderView";
import CustomInputWithUnit from "../../../components/CustomInputWithUnit";
import useDebounce from "../../../utils/useDebounce";

const InternalAddPaperOrderPage = () => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const printRef = useRef(null);

  //  STATES
  const [selectedRulers, setSelectedRulers] = useState([]);
  const [selectedColors, setSelectedColors] = useState({});
  const [allSelectedRulers, setAllSelectedRulers] = useState([]);
  const [dataToPrint, setDataToPrint] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  // const [orderDetails, setOrderDetails] = useState({
  //   orderNumber: "",
  //   date: new Date().toLocaleDateString(),
  //   companyName: "",
  //   createdBy: "",
  // });
  const [showAreYouSure, setAreYouSure] = useState(false);
  const [orderNotes, setOrderNotes] = useState("");

  const [loadingRulers, setLoadingRuler] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [dimensionSearch, setDimensionSearch] = useState("");
  const [paperThicknessSearch, setPaperThicknessSearch] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [fabricData, setFabricData] = useState([]);
  const [orderData, setOrderData] = useState({
    orderNumber: null,
    date: new Date(),
    companyName: "",
    createdBy: "",
    SON: "",
  });
  const [selectedCurrency, setSelectedCurrency] = useState({
    title: "usd",
    value: "USD",
  });

  const [selectedMeasuringUnit, setSelectedMeasuringUnit] = useState({
    title: "meter",
    value: "METER",
  });

  const [collapsedRulers, setCollapsedRulers] = useState({});

  /* ENDPOINTS */

  const {
    loading: confirmOrderLoading,
    success,
    error,
    setError,
    data,
    refetch: handleSaveOrder,
  } = useRequest(
    `createFabricOrder`,
    "POST",
    {
      colors: allSelectedRulers
        ?.map((ruler) =>
          ruler?.colors.map((color) => ({
            ...color,
            measuringUnit: selectedMeasuringUnit.value,
            currency: selectedCurrency.value,
          }))
        )
        ?.flat(),
      organizationId: parseInt(userData?.organizationId),
      userId: parseInt(userData?.userId),
      orderNumber: orderData?.orderNumber,
      createdAt: new Date(orderData?.date),
      measuringUnit: selectedMeasuringUnit.value,
      currency: selectedCurrency.value,
    },
    {},
    false
  );

  const createPaperOrder = async () => {
    try {
      // Validate organizationId
      const organizationId = parseInt(userData?.organizationId);
      if (!organizationId || isNaN(organizationId)) {
        throw new Error("Valid organization ID is required");
      }

      // Validate we have the required data
      if (!Array.isArray(allSelectedRulers) || allSelectedRulers.length === 0) {
        throw new Error("At least one ruler item is required");
      }

      // Transform the items data (without notes)
      const orderItems = allSelectedRulers.flatMap((ruler) => {
        if (
          !ruler.colors ||
          !Array.isArray(ruler.colors) ||
          ruler.colors.length === 0
        ) {
          return []; // Skip rulers with no colors
        }

        return ruler.colors.map((color) => {
          // Validate required fields
          if (!color.colorName || color.colorName.trim() === "") {
            throw new Error("Color name is required for each item");
          }

          return {
            rollCode: ruler.code?.trim() || null,
            paperCode: color.colorCode?.trim() || null,
            paperName: color.colorName.trim(),
            measuringUnit: ruler.measuringUnit?.trim() || null,
            dimension: color.dimension || null,
            amount: color.quantity || 0,
            amountOfRolls: color.amountOfRolls || 0,
            paperThickness: color.paperThickness || null,
            paperType: color.paperType || null,
          };
        });
      });

      if (orderItems.length === 0) {
        throw new Error("No valid paper items to order");
      }

      // Prepare the complete payload

      const payload = {
        orderNumber: orderData?.SON || undefined, // Optional
        notes: orderNotes?.trim() || undefined, // Now at parent level
        organizationId,
        createdBy: userData?.username,
        orderDate: orderData?.date || new Date(),
        orderItems: orderItems.map((item) => ({
          ...item,
          amount: parseFloat(item.amount),
          amountOfRolls: parseFloat(item.amountOfRolls),
          paperThickness: parseFloat(item.paperThickness),
        })),
      };

      const printData = {
        SON: orderData?.SON,
        orderItems: allSelectedRulers.flatMap((ruler) =>
          ruler.colors.map((color) => ({
            paperCode: ruler.code,
            paperName: color.colorCode,
            dimension: color.dimension,
            amount: color.quantity,
            amountOfRolls: color.amountOfRolls,
            paperThickness: color.paperThickness,
            paperType: color.paperType || "",
          }))
        ),
      };

      setDataToPrint(printData);

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/api/paper`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 201) {
        setAllSelectedRulers([]);
        setSelectedColors({});
        setOrderNotes("");
        getAllRulers();
      }

      return response.data;
    } catch (error) {
      console.error("Failed to create paper order:", error);
      throw error;
    }
  };

  const handlePrintAndSave = async () => {
    setAreYouSure(false);
    try {
      await createPaperOrder();
      handlePrint();
    } catch (error) {
      console.error("Error saving order:", error);
    }
  };

  const getAllRulers = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingRuler(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllfabricRulers`,
        {
          params: {
            organizationId,
            code: searchQuery || undefined,
            paperThickness: parseFloat(paperThicknessSearch) || undefined,
            dimension: parseFloat(dimensionSearch) || undefined,
            page: pagination.currentPage,
            limit: 5,
          },
        }
      );

      if (response.status === 200 && response.data?.data) {
        const formatted = response.data.data.map((item) => ({
          id: item.id,
          code: item.code,
          colors: item.fabricColor.map((c) => ({
            id: c.id,
            colorCode: c.colorCode,
            colorName: c.colorName,
            paperType: "",
          })),
        }));
        setFabricData(formatted);
        setOrderData({
          orderNumber: response?.data?.orderNumber,
          date: response?.data?.date,
          companyName: userData.organizationName,
          createdBy: userData.username,
          SON: response?.data.SON,
        });

        setPagination({
          currentPage: response.data?.pagination?.page,
          totalPages: response.data?.pagination?.totalPages,
          totalRecords: response.data?.pagination?.total,
        });
      }
    } catch (err) {
      console.error("Failed to fetch rulers:", err);
    } finally {
      setLoadingRuler(false);
    }
  };

  /* HANDLER FUNCTIONS */
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page { 
        size: A4; 
        margin: 5mm;
        width: 21cm;
        height: 29.7cm;
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact;
          padding: 10mm;
        }
        .printable-order-view { display: flex !important; }
      }
    `,
  });

  const handleConfirmOrder = () => {
    setAreYouSure(true);
  };

  // Updated color selection handler to maintain allSelectedRulers
  const handleColorSelection = (
    fabricCode,
    colorCode,
    isSelected,
    isFromSearch = false
  ) => {
    setSelectedColors((prev) => {
      if (colorCode === null) {
        const { [fabricCode]: removed, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [fabricCode]: {
          ...prev[fabricCode],
          [colorCode]: isSelected,
        },
      };
    });

    if (colorCode === null) {
      setAllSelectedRulers((prev) =>
        prev.filter((ruler) => ruler.code !== fabricCode)
      );
    } else {
      if (isSelected) {
        const fabric =
          fabricData.find((f) => f.code === fabricCode) ||
          allSelectedRulers.find((r) => r.code === fabricCode);

        if (fabric) {
          const color = fabric.colors.find((c) => c.colorCode === colorCode);

          if (color) {
            setAllSelectedRulers((prev) => {
              const rulerIndex = prev.findIndex((r) => r.code === fabricCode);

              if (rulerIndex >= 0) {
                const colorExists = prev[rulerIndex].colors.some(
                  (c) => c.colorCode === colorCode
                );

                if (!colorExists) {
                  const updatedRulers = [...prev];
                  updatedRulers[rulerIndex] = {
                    ...updatedRulers[rulerIndex],
                    colors: [
                      ...updatedRulers[rulerIndex].colors,
                      {
                        colorId: color.id,
                        colorCode: color.colorCode,
                        colorName: color.colorName,
                        fabricRulerId: fabric.id,
                        quantity: 0,
                        price: 0,
                      },
                    ],
                  };
                  return updatedRulers;
                }
                return prev;
              } else {
                return [
                  ...prev,
                  {
                    id: fabric.id,
                    code: fabricCode,
                    colors: [
                      {
                        colorId: color.id,
                        colorCode: color.colorCode,
                        colorName: color.colorName,
                        fabricRulerId: fabric.id,
                        quantity: 0,
                        price: 0,
                      },
                    ],
                  },
                ];
              }
            });
          }
        }
      } else {
        setAllSelectedRulers((prev) => {
          const updatedRulers = prev.map((ruler) => {
            if (ruler.code === fabricCode) {
              return {
                ...ruler,
                colors: ruler.colors.filter(
                  (color) => color.colorCode !== colorCode
                ),
              };
            }
            return ruler;
          });

          // Remove rulers with no colors
          return updatedRulers.filter((ruler) => ruler.colors.length > 0);
        });
      }
    }
  };

  // Update input values for the selected colors
  const handleInputChange = (fabricCode, colorCode, field, value) => {
    setAllSelectedRulers((prev) => {
      return prev.map((ruler) => {
        if (ruler.code === fabricCode) {
          return {
            ...ruler,
            colors: ruler.colors.map((color) => {
              if (color.colorCode === colorCode) {
                return {
                  ...color,
                  [field]: value || 0,
                };
              }
              return color;
            }),
          };
        }
        return ruler;
      });
    });
  };

  /* USEEFECTS */
  useEffect(() => {
    getAllRulers();
  }, [debouncedSearch, pagination.currentPage]);

  useEffect(() => {
    if (success) {
      handlePrint();
      setOrderData({
        orderNumber: null,
        date: new Date(),
        companyName: "",
        createdBy: "",
      });
      setAllSelectedRulers([]);
      setSelectedColors({});
      getAllRulers();
      setSearchQuery("");
    }
  }, [success]);

  // Sync selected rulers to display from our master list
  useEffect(() => {
    setSelectedRulers(allSelectedRulers);
  }, [allSelectedRulers]);

  return (
    <PageWrapper>
      {/* Back button */}
      <div>
        <BackToDashboard
          title={t("back")}
          variant="blank"
          icon={currentDirection === "ltr" ? previousIcon : nextIcon}
          iconSize={14}
          to={
            userData?.userType === "user"
              ? "/invoiceManagement"
              : "/paperfactory-dashboard"
          }
        />
      </div>
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500">
        <div className="o-column o-column--gap-size_400">
          <PageHeadline
            title="addInternalOrder"
            classList="u-margin--top_700"
          />
          <div className="o-row o-row--gap-size_300">
            <div>
              <label className="t-bold_semi-bold">{t("orderNumber")}: </label>
              <CustomInput
                value={orderData?.SON}
                flex={false}
                readonly
                loading={loadingRulers}
              />
            </div>
            <div>
              <label className="t-bold_semi-bold">{t("Date")}: </label>
              <CustomInput
                type="date"
                value={orderData?.date}
                onChange={(e) =>
                  setOrderData((prev) => ({ ...prev, date: e.target.value }))
                }
                flex={false}
                loading={loadingRulers}
              />
            </div>
          </div>
        </div>

        <section className="o-row o-row--gap-size_300 multiple-search-filter o-column-row-switcher u-width_full-width">
          <div className="o-column o-column--gap-size_300 multiple-search-filter-dates ">
            <div className="u-width_full-width">
              <label className="t-bold_semi-bold">{t("searchByCode")}: </label>
              <CustomInput
                placeholder={t("search")}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                resetSearch={() => {
                  setSearchQuery("");
                }}
                thick
              />
            </div>
            <div className="o-row o-row--gap-size_400 o-row--vertical-align_end o-column-row-switcher">
              <div className="o-row o-row--gap-size_200">
                <div>
                  <label className="t-bold_semi-bold">{t("dimension")}: </label>
                  <CustomInputWithUnit
                    placeholder={t("search")}
                    value={dimensionSearch}
                    onChange={(e) => {
                      setDimensionSearch(e.target.value);
                    }}
                    resetSearch={() => {
                      setDimensionSearch("");
                    }}
                    thick
                    unit={t("cm")}
                  />
                </div>

                <div>
                  <label className="t-bold_semi-bold">
                    {t("paperThickness")}:
                  </label>
                  <CustomInputWithUnit
                    placeholder={t("search")}
                    value={paperThicknessSearch}
                    onChange={(e) => {
                      setPaperThicknessSearch(e.target.value);
                    }}
                    resetSearch={() => {
                      setPaperThicknessSearch("");
                    }}
                    thick
                    unit={t("gram")}
                  />
                </div>
              </div>
              <CustomButton
                title={t("search")}
                iconSize={15}
                classList="c-search-button"
                icon={searchIcon}
                onClick={() => getAllRulers()}
              />
            </div>
          </div>
        </section>

        <SectionDivider title={t("categories")} />

        {loadingRulers && <Loader />}

        <InternalPaperOrderListWithInput
          data={fabricData}
          setSelectedRulers={setSelectedRulers}
          selectedRulers={selectedRulers}
          selectedColors={selectedColors}
          onColorSelect={handleColorSelection}
          measuringUnit={selectedMeasuringUnit}
          currency={selectedCurrency}
          onInputChange={handleInputChange}
          allSelectedRulers={allSelectedRulers}
          collapsedRulers={collapsedRulers}
          setCollapsedRulers={setCollapsedRulers}
        />

        {pagination && (
          <section className="pagination">
            <div className="pagination-buttons">
              <CustomButton
                variant="blank"
                title={t("previous")}
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage - 1,
                  }))
                }
                disabled={pagination.currentPage === 1}
                icon={currentDirection === "ltr" ? previousIcon : nextIcon}
                iconSize={15}
              />
              <span className="pagination-location">
                {t("page")}: {pagination.currentPage} - {pagination.totalPages}
              </span>
              <CustomButton
                title={t("next")}
                variant="blank"
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage + 1,
                  }))
                }
                disabled={pagination.currentPage === pagination.totalPages}
                icon={currentDirection === "ltr" ? nextIcon : previousIcon}
                iconSize={15}
                reverseIcon
              />
            </div>
            <div className="pagination-buttons">
              <span className="pagination-location">
                {t("totalRecords")}: {pagination.totalRecords}
              </span>
            </div>
          </section>
        )}

        {allSelectedRulers.length > 0 && (
          <SectionDivider title={t("addedCategories")} />
        )}
        <InternalPaperRulers
          data={allSelectedRulers}
          setSelectedRulers={setAllSelectedRulers}
          selectedColors={selectedColors}
          onColorSelect={handleColorSelection}
          measuringUnit={selectedMeasuringUnit}
          currency={selectedCurrency}
          onInputChange={handleInputChange}
          collapsedRulers={[]}
          setCollapsedRulers={() => {}}
        />

        {allSelectedRulers.length !== 0 && (
          <div className="o-column u-width_full-width o-column--gap-size_100">
            <span>{t("notes")}: </span>
            <CustomInput
              type="textarea"
              placeholder={t("notes")}
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              flex
            />
          </div>
        )}

        {confirmOrderLoading && <Loader />}
        <div className="o-row o-row--horizontal-align_end o-row--gap-size_400">
          {allSelectedRulers.length > 0 && (
            <>
              <CustomButton
                title={t("confirm")}
                onClick={handleConfirmOrder}
                icon={saveIcon}
                iconSize={16}
                disabled={confirmOrderLoading || isPrinting}
              />
            </>
          )}
        </div>
      </div>

      {showAreYouSure && (
        <AreYouSureModal
          onClose={() => setAreYouSure(false)}
          onConfirm={handlePrintAndSave}
          customMessage={t("confirmOrderMessage")}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirm={true}
          confirmIconType="whiteCheck"

        />
      )}

      <InternalPrintableOrderView
        ref={printRef}
        data={dataToPrint || []}
        currency={selectedCurrency}
        measuringUnit={selectedMeasuringUnit}
        orderDetails={{
          ...orderData,
          date: formatDate(orderData?.date),
          notes: orderNotes,
        }}
      />
    </PageWrapper>
  );
};

export default InternalAddPaperOrderPage;
